import React, { useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';

export interface TvFocusableInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  multiline?: boolean;
  rows?: number;
  className?: string;
  wrapperClassName?: string;
  placeholder?: string;
  type?: string;
  inputMode?: 'text' | 'numeric' | 'email' | 'tel' | 'search' | 'url' | 'none' | 'decimal';
  enterKeyHint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send';
  maxLength?: number;
  minLength?: number;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onInputChange?: (value: string) => void;
  style?: React.CSSProperties;
}

export interface TvFocusableInputHandle {
  focusWrapper: () => void;
  focusInput: () => void;
  getWrapperEl: () => HTMLDivElement | null;
}

function getVisibleTvFocusables(): HTMLElement[] {
  const els = Array.from(document.querySelectorAll<HTMLElement>('[data-tv-focusable]'));
  return els.filter(el => {
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });
}

function moveTvFocus(direction: 'up' | 'down', currentEl: HTMLElement) {
  const all = getVisibleTvFocusables();
  const idx = all.indexOf(currentEl);
  if (idx === -1) return;
  const nextIdx = direction === 'down' ? idx + 1 : idx - 1;
  if (nextIdx >= 0 && nextIdx < all.length) {
    all[nextIdx].focus();
    all[nextIdx].scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
}

export const TvFocusableInput = forwardRef<TvFocusableInputHandle, TvFocusableInputProps>(
  (
    {
      value,
      onChange,
      multiline = false,
      rows = 3,
      className = '',
      wrapperClassName = '',
      placeholder,
      type = 'text',
      inputMode,
      enterKeyHint,
      maxLength,
      minLength,
      required,
      disabled,
      autoFocus,
      onPaste,
      style,
    },
    ref
  ) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [isEditing, setIsEditing] = useState(false);

    useImperativeHandle(ref, () => ({
      focusWrapper: () => wrapperRef.current?.focus(),
      focusInput: () => activateInput(),
      getWrapperEl: () => wrapperRef.current,
    }));

    const activateInput = useCallback(() => {
      setIsEditing(true);
      setTimeout(() => {
        const el = multiline ? textareaRef.current : inputRef.current;
        if (el) {
          el.focus();
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 0);
    }, [multiline]);

    const deactivateInput = useCallback(() => {
      setIsEditing(false);
      setTimeout(() => wrapperRef.current?.focus(), 0);
    }, []);

    const handleWrapperKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activateInput();
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          moveTvFocus('down', e.currentTarget);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          moveTvFocus('up', e.currentTarget);
        }
      },
      [activateInput]
    );

    const handleInputKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          deactivateInput();
          return;
        }

        if (!multiline && e.key === 'Enter') {
          e.preventDefault();
          deactivateInput();
          return;
        }

        if (!multiline) {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            deactivateInput();
            setTimeout(() => {
              if (wrapperRef.current) {
                moveTvFocus(e.key === 'ArrowDown' ? 'down' : 'up', wrapperRef.current);
              }
            }, 10);
            return;
          }
        }

        if (multiline) {
          if (e.key === 'ArrowUp') {
            const el = e.currentTarget as HTMLTextAreaElement;
            if (el.selectionStart === 0 && el.selectionEnd === 0) {
              e.preventDefault();
              deactivateInput();
              setTimeout(() => {
                if (wrapperRef.current) {
                  moveTvFocus('up', wrapperRef.current);
                }
              }, 10);
            }
          } else if (e.key === 'ArrowDown') {
            const el = e.currentTarget as HTMLTextAreaElement;
            if (el.selectionStart === el.value.length) {
              e.preventDefault();
              deactivateInput();
              setTimeout(() => {
                if (wrapperRef.current) {
                  moveTvFocus('down', wrapperRef.current);
                }
              }, 10);
            }
          }
        }
      },
      [multiline, deactivateInput]
    );

    const handleInputBlur = useCallback(() => {
      setIsEditing(false);
    }, []);

    const sharedProps = {
      value,
      onChange,
      className: `${className} outline-none`,
      placeholder,
      maxLength,
      minLength,
      required,
      disabled,
      onKeyDown: handleInputKeyDown,
      onBlur: handleInputBlur,
      onPaste,
      style,
      tabIndex: -1,
    };

    return (
      <div
        ref={wrapperRef}
        tabIndex={disabled ? -1 : 0}
        data-tv-focusable=""
        data-tv-active={isEditing ? '' : undefined}
        className={`tv-focusable-wrapper ${isEditing ? 'tv-editing' : ''} ${wrapperClassName}`}
        onKeyDown={!isEditing ? handleWrapperKeyDown : undefined}
        autoFocus={autoFocus}
      >
        {multiline ? (
          <textarea
            ref={textareaRef}
            rows={rows}
            enterKeyHint={enterKeyHint}
            {...sharedProps}
          />
        ) : (
          <input
            ref={inputRef}
            type={type}
            inputMode={inputMode}
            enterKeyHint={enterKeyHint}
            {...sharedProps}
          />
        )}
      </div>
    );
  }
);

TvFocusableInput.displayName = 'TvFocusableInput';

export default TvFocusableInput;
